/* Runtime config for the landing module.
   Mirrors team-grid-react-app-new/src/utils/serverConfig.js so that, once this
   folder is dropped into the host app, API calls resolve to exactly the same
   origin the rest of the app uses. */

/* ---------------------------------------------------------------------------
   Reading REACT_APP_* variables.

   CRA/webpack replaces the LITERAL TEXT `process.env.REACT_APP_FOO` at build
   time. There is no `process` object in the browser afterwards, so anything
   that reads the value indirectly gets nothing:

       const env = typeof process !== 'undefined' ? process.env : undefined;
       env?.REACT_APP_RECAPTCHA_SITE_KEY        // always undefined in CRA

   That guard was here to keep Vite happy, and it silently emptied every
   variable in the host build — the reCAPTCHA widget never rendered, so sign-up
   posted no token and the API answered "Please complete the reCAPTCHA".

   So each variable is read as its own literal expression, wrapped in a
   try/catch: webpack substitutes the text, and under Vite (where `process`
   genuinely doesn't exist) the ReferenceError is caught and we fall back.
   --------------------------------------------------------------------------- */
function fromEnv(read) {
  try {
    const v = read();
    return typeof v === 'string' && v ? v : '';
  } catch {
    return ''; // Vite / any bundler that doesn't define process
  }
}

/** Window override, settable before React mounts. */
function fromWindow(key) {
  try {
    return typeof window !== 'undefined' && window[key] ? String(window[key]) : '';
  } catch {
    return '';
  }
}

export const SERVER_URL = (() => {
  // Explicit override without touching code:  window.__TG_SERVER_URL__ = '…'
  const w = fromWindow('__TG_SERVER_URL__');
  if (w) return w.replace(/\/$/, '');

  // Runtime override, no rebuild — point the site at a staging/mock API:
  //   localStorage.setItem('tg_server_url', 'http://localhost:3999')
  try {
    const ls = typeof window !== 'undefined' && window.localStorage?.getItem('tg_server_url');
    if (ls) return String(ls).replace(/\/$/, '');
  } catch { /* private mode */ }

  const explicit = fromEnv(() => process.env.REACT_APP_SERVER_URL);
  if (explicit) return explicit.replace(/\/$/, '');

  /* Mirrors the host's serverConfig.js EXACTLY: localhost:3001 in development,
     the page's own origin in production.

     Do not "simplify" this to window.location.origin in dev. The host's
     package.json sets `proxy: https://teamgrid.ai`, and setupProxy.js only
     redirects /api/v1/{crm,users,projects,stealth-agent} to the local backend.
     Everything else — including /auth/* and /contact — falls through to
     PRODUCTION. Using the page origin here would mean a local test signup
     creating a real account on the live site. */
  if (fromEnv(() => process.env.NODE_ENV) === 'development') return 'http://localhost:3001';

  if (typeof window !== 'undefined') return window.location.origin;
  return '';
})();

/** Canonical origin used for <link rel="canonical"> and og:url. */
export const SITE_URL = 'https://teamgrid.ai';

/** Where a successful sign-in should land the user in the host app. */
export const AFTER_AUTH_REDIRECT = '/dashboard';

/** Google OAuth client id — same env var the host's GoogleOAuthProvider reads.
 *  Empty (or the placeholder) means Google sign-in isn't configured, and the
 *  button is hidden rather than shown as something that silently does nothing. */
export const GOOGLE_CLIENT_ID = (() => {
  const id = fromWindow('__TG_GOOGLE_CLIENT_ID__') || fromEnv(() => process.env.REACT_APP_GOOGLE_CLIENT_ID);
  return id === 'your_google_client_id_here' ? '' : id;
})();

/** Google reCAPTCHA site key. The backend VALIDATES this on /auth/register and
 *  on both shapes of /api/v1/contact, so those forms fail without it. Reads the
 *  same env var the host already uses. */
export const RECAPTCHA_SITE_KEY =
  fromWindow('__TG_RECAPTCHA_SITE_KEY__') || fromEnv(() => process.env.REACT_APP_RECAPTCHA_SITE_KEY);

/** Cal.com booking link used by /book-demo and /managed-services/book. */
export const CALCOM_EMBED_LINK =
  fromEnv(() => process.env.REACT_APP_CALCOM_EMBED_LINK) || 'teamgrid-sales-bd6ovp/30min';

/* AWS_CONFIG was removed. It existed only to feed the Background Agent's
   browser-side S3-presign fallback, which required an IAM key pair in the
   bundle. Downloads now go through the backend's presigned redirect at
   /api/v1/stealth-agent/downloads/<key> — see utils/agentDownloads.js. */
